import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../utils/supabase';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const VAQuickActionsNav = ({ activeSection }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [interviewCount, setInterviewCount] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  const quickActions = [
    {
      id: 'search-jobs',
      label: 'Find Jobs',
      icon: 'Search',
      path: '/job-search',
      color: 'bg-primary text-white',
      count: 0
    },
    {
      id: 'messages',
      label: 'Messages',
      icon: 'MessageSquare',
      path: '/messages',
      color: 'bg-accent text-white',
      count: messageCount
    },
    {
      id: 'applications',
      label: 'Applications',
      icon: 'File',
      path: '/applications',
      color: 'bg-blue-500 text-white',
      count: 0
    },
    {
      id: 'interviews',
      label: 'Interviews',
      icon: 'Calendar',
      path: '/interviews',
      color: 'bg-warning text-white',
      count: interviewCount
    },
    {
      id: 'skills',
      label: 'Skills',
      icon: 'Star',
      path: '/up-skills',
      color: 'bg-indigo-500 text-white',
      count: 0
    },
    {
      id: 'projects',
      label: 'My Contracts',
      icon: 'Briefcase',
      path: '/my-contracts',
      color: 'bg-teal-500 text-white',
      count: 0
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: 'User',
      path: '/va-profile',
      color: 'bg-success text-white',
      count: 0
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: 'BarChart2',
      path: '/analytics',
      color: 'bg-purple-500 text-white',
      count: 0
    }
  ];

  // Fetch initial counts
  const fetchInitialCounts = useCallback(async () => {
    if (!user?.id) return;

    try {
      // Fetch unread message count
      const { count: messageCount, error: messageError } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', user.id)
        .eq('is_read', false);

      if (!messageError) {
        setMessageCount(messageCount || 0);
      }

      // Fetch upcoming interview count
      const now = new Date().toISOString();
      const { count: interviewCount, error: interviewError } = await supabase
        .from('interview_schedules')
        .select('*', { count: 'exact', head: true })
        .eq('candidate_id', user.id)
        .eq('status', 'scheduled')
        .gte('scheduled_at', now);

      if (!interviewError) {
        setInterviewCount(interviewCount || 0);
      }
    } catch (error) {
      console.error('Error fetching initial counts:', error);
    }
  }, [user?.id]);

  // Set up realtime subscriptions
  useEffect(() => {
    if (!user?.id) return;

    fetchInitialCounts();

    // Subscribe to message changes
    const messagesChannel = supabase
      .channel('nav_messages')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'messages',
          filter: `recipient_id=eq.${user.id}`
        }, 
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          
          if (eventType === 'INSERT' && !newRecord.is_read) {
            setMessageCount(prev => prev + 1);
          } else if (eventType === 'UPDATE') {
            // If message was marked as read
            if (oldRecord.is_read === false && newRecord.is_read === true) {
              setMessageCount(prev => Math.max(0, prev - 1));
            }
            // If message was marked as unread
            else if (oldRecord.is_read === true && newRecord.is_read === false) {
              setMessageCount(prev => prev + 1);
            }
          } else if (eventType === 'DELETE' && !oldRecord.is_read) {
            setMessageCount(prev => Math.max(0, prev - 1));
          }
        }
      )
      .subscribe();

    // Subscribe to interview changes
    const interviewsChannel = supabase
      .channel('nav_interviews')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'interview_schedules',
          filter: `candidate_id=eq.${user.id}`
        }, 
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;
          const now = new Date();
          
          if (eventType === 'INSERT') {
            // Check if it's a scheduled interview in the future
            if (newRecord.status === 'scheduled' && new Date(newRecord.scheduled_at) > now) {
              setInterviewCount(prev => prev + 1);
            }
          } else if (eventType === 'UPDATE') {
            const wasUpcoming = oldRecord.status === 'scheduled' && new Date(oldRecord.scheduled_at) > now;
            const isUpcoming = newRecord.status === 'scheduled' && new Date(newRecord.scheduled_at) > now;
            
            if (wasUpcoming && !isUpcoming) {
              setInterviewCount(prev => Math.max(0, prev - 1));
            } else if (!wasUpcoming && isUpcoming) {
              setInterviewCount(prev => prev + 1);
            }
          } else if (eventType === 'DELETE') {
            // Check if deleted interview was upcoming
            if (oldRecord.status === 'scheduled' && new Date(oldRecord.scheduled_at) > now) {
              setInterviewCount(prev => Math.max(0, prev - 1));
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(interviewsChannel);
    };
  }, [user?.id, fetchInitialCounts]);

  const handleActionClick = (path) => {
    // Clear counters when navigating to the respective page
    if (path === '/messages') {
      setMessageCount(0);
    } else if (path === '/interviews') {
      setInterviewCount(0);
    }
    
    navigate(path);
    setIsExpanded(false);
  };

  return (
    <>
      {/* Desktop Quick Actions */}
      <div className="hidden md:block">
        <div className="card bg-surface p-4 shadow-sm rounded-lg border border-border">
          <h3 className="font-semibold text-text-primary mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {quickActions.map((action) => (
              <Button
                key={action.id}
                variant="outline"
                onClick={() => handleActionClick(action.path)}
                className={`relative flex flex-col items-center p-4 h-auto hover:bg-secondary-50 border border-border ${
                  activeSection === action.id ? 'bg-secondary-50 border-primary' : ''
                }`}
              >
                <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center mb-2`}>
                  <Icon name={action.icon} size={20} />
                </div>
                <span className="text-sm font-medium text-text-primary">
                  {action.label}
                </span>
                {action.count > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                    {action.count > 99 ? '99+' : action.count}
                  </span>
                )}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* 
        TODO: FUTURE IMPROVEMENT - Mobile Floating Action Button
        This component needs refinement to work properly with the mobile layout.
        Currently removed due to layout width issues (473px vs 425px viewport).
        When implementing in the future:
        1. Fix z-index conflicts
        2. Ensure proper positioning relative to viewport
        3. Test on various mobile screen sizes
        4. Ensure proper width constraints for mobile devices
      */}
    </>
  );
};

export default VAQuickActionsNav;
